/**
 * OmniLeagues - Birthday Tracker Panel
 * Shows player birthdays, age-at-cutoff, qualifying team, and upcoming birthdays.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Cake, Loader2, Calendar, Users, AlertCircle
} from 'lucide-react';
import { SearchBox, useDebounced, matchesAny } from './familyDirectoryShared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BirthdayTrackerPanel({ orgId, teams, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamFilter, setTeamFilter] = useState('');
  const [seasonFilter, setSeasonFilter] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [search, setSearch] = useState('');
  const seasonInit = useRef(false);
  const q = useDebounced(search, 250);

  const fetchData = useCallback(async () => {
    if (!orgId) return;
    try {
      const params = new URLSearchParams();
      if (teamFilter)   params.set('team_id',  teamFilter);
      if (seasonFilter) params.set('season',   seasonFilter);
      if (sportFilter)  params.set('sport_id', sportFilter);
      if (levelFilter)  params.set('level',    levelFilter);
      const { data } = await axios.get(`${API_URL}/api/exports/birthdays/${orgId}?${params}`);
      setData(data);
      // Default the season filter to the calculated current season once (never a
      // literal year); the user can still switch to "All Seasons" afterwards.
      if (!seasonInit.current) {
        seasonInit.current = true;
        const opts = data.season_options || [];
        if (data.current_season && opts.includes(data.current_season)) setSeasonFilter(data.current_season);
      }
    } catch (err) {
      console.error('Failed to load birthday data:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, teamFilter, seasonFilter, sportFilter, levelFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  if (!data) return null;

  // A specific PAST season (not "All", not the current season). Past-season
  // views show only Player · Birthday · current Age — team/eligibility columns
  // and the mismatch banner are hidden (they only make sense for the current
  // roster). (migration_log_0124)
  const isPast = !!seasonFilter && !!data.current_season && seasonFilter !== data.current_season;

  // Client-side, debounced, focus-stable substring search on player name +
  // team (never refetches). (migration_log_0124)
  const filteredBirthdays = data.birthdays.filter(b =>
    matchesAny(q, [b.name, b.team, b.qualifying_team])
  );

  const todayBirthdays = data.birthdays.filter(b => b.birthday_today);
  const upcoming = data.birthdays.filter(b => b.upcoming && !b.birthday_today);
  // A row is only a real mismatch when the player's CURRENT TEAM LEVEL
  // differs from their age-derived qualifying level (Middle School / JV /
  // Varsity). Comparing the raw team name ("Middle School Boys") against
  // the level label ("Middle School") would always fire — that was the
  // legacy bug. Backend now projects `team_level` per row.
  const mismatchedTeam = data.birthdays.filter(b =>
    b.team_level && b.qualifying_team && b.team !== 'Unassigned'
    && b.qualifying_team !== 'N/A' && b.team_level !== b.qualifying_team
  );

  return (
    <div className="space-y-3">
      {/* Stats */}
      <div className="flex gap-3">
        {todayBirthdays.length > 0 && (
          <Card className={`flex-1 ${isDark ? 'bg-amber-900/30 border-amber-700' : 'bg-amber-50 border-amber-200'}`}>
            <CardContent className="p-3 flex items-center gap-2">
              <Cake className="h-5 w-5 text-amber-500" />
              <div>
                <p className={`text-sm font-bold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>Today's Birthdays!</p>
                <p className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                  {todayBirthdays.map(b => b.name).join(', ')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        {!isPast && mismatchedTeam.length > 0 && (
          <Card className={`flex-1 ${isDark ? 'bg-orange-900/30 border-orange-700' : 'bg-orange-50 border-orange-200'}`}>
            <CardContent className="p-3 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              <div>
                <p className={`text-sm font-bold ${isDark ? 'text-orange-300' : 'text-orange-800'}`}>{mismatchedTeam.length} Team-Age Mismatches</p>
                <p className={`text-xs ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>Players on teams different from their qualifying team</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search players or team…"
          isDark={isDark}
          testId="birthday-search"
          className="min-w-[220px]"
        />
        <select
          value={seasonFilter}
          onChange={(e) => { setSeasonFilter(e.target.value); setLoading(true); }}
          className={`rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300'}`}
          data-testid="birthday-season-filter"
        >
          <option value="">All Seasons</option>
          {(data.season_options || []).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={sportFilter}
          onChange={(e) => { setSportFilter(e.target.value); setLoading(true); }}
          className={`rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300'}`}
          data-testid="birthday-sport-filter"
        >
          <option value="">All Sports</option>
          {(data.sport_options || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select
          value={levelFilter}
          onChange={(e) => { setLevelFilter(e.target.value); setLoading(true); }}
          className={`rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300'}`}
          data-testid="birthday-level-filter"
        >
          <option value="">All Levels</option>
          {(data.level_options || []).map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
        <select
          value={teamFilter}
          onChange={(e) => { setTeamFilter(e.target.value); setLoading(true); }}
          className={`rounded-md px-3 py-2 text-sm border ${isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-300'}`}
          data-testid="birthday-team-filter"
        >
          <option value="">All Teams ({data.birthdays.length} players)</option>
          {(teams || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {data.upcoming_count} upcoming (next 30 days)
        </span>
      </div>

      {/* Table */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'border-b border-slate-700' : 'border-b border-slate-200'}>
                {(isPast ? ['Player', 'Birthday', 'Age'] : ['Player', 'Current Team', 'Birthday', 'Age', 'Age@Cutoff', 'Qualifying Team', 'Days']).map(h => (
                  <th key={h} className={`px-3 py-2 text-left text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredBirthdays.length === 0 && (
                <tr>
                  <td colSpan={isPast ? 3 : 7} className={`py-6 text-center italic ${isDark ? 'text-slate-400' : 'text-slate-500'}`} data-testid="birthday-empty">
                    No players match this filter.
                  </td>
                </tr>
              )}
              {filteredBirthdays.map((b, i) => {
                const isMismatch = b.team_level && b.qualifying_team && b.team !== 'Unassigned'
                  && b.qualifying_team !== 'N/A' && b.team_level !== b.qualifying_team;
                // Row shading by sport level (Session 19, May 12 2026 v2).
                // Subtle band so the AD can pattern-match age cohorts at a
                // glance without overwhelming the rest of the table.
                const levelTint = (() => {
                  switch (b.qualifying_team) {
                    case 'Middle School':
                      return isDark ? 'bg-sky-900/15' : 'bg-sky-50/60';
                    case 'Junior Varsity':
                      return isDark ? 'bg-violet-900/15' : 'bg-violet-50/60';
                    case 'Varsity':
                      return isDark ? 'bg-emerald-900/15' : 'bg-emerald-50/60';
                    case 'Ineligible':
                      return isDark ? 'bg-red-900/20' : 'bg-red-50/70';
                    default:
                      return '';
                  }
                })();
                const todayTint = b.birthday_today
                  ? (isDark ? 'bg-amber-900/30' : 'bg-amber-100/70')
                  : '';
                if (isPast) {
                  return (
                    <tr key={i} className={`border-b ${isDark ? 'border-slate-700/50' : 'border-slate-100'} ${todayTint}`} data-testid={`birthday-row-${i}`}>
                      <td className={`px-3 py-2 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {b.birthday_today && <Cake className="inline h-3 w-3 text-amber-500 mr-1" />}
                        {b.name}
                      </td>
                      <td className={`px-3 py-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{b.birthday}</td>
                      <td className={`px-3 py-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{b.current_age}</td>
                    </tr>
                  );
                }
                return (
                  <tr
                    key={i}
                    className={`border-b ${isDark ? 'border-slate-700/50' : 'border-slate-100'} ${todayTint || levelTint}`}
                    data-testid={`birthday-row-${i}`}
                  >
                    <td className={`px-3 py-2 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {b.birthday_today && <Cake className="inline h-3 w-3 text-amber-500 mr-1" />}
                      {b.name}
                    </td>
                    <td className={`px-3 py-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{b.team}</td>
                    <td className={`px-3 py-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{b.birthday}</td>
                    <td className={`px-3 py-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{b.current_age}</td>
                    <td className={`px-3 py-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{b.age_at_cutoff}</td>
                    <td className="px-3 py-2">
                      <Badge className={
                        b.qualifying_team === 'Ineligible'
                          ? 'bg-red-500 text-white'
                          : isMismatch
                            ? 'bg-orange-500 text-white'
                            : (isDark ? 'bg-slate-600' : 'bg-slate-200')
                      }>
                        {b.qualifying_team}
                      </Badge>
                    </td>
                    <td className={`px-3 py-2 ${b.upcoming ? 'text-amber-500 font-semibold' : isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {b.days_until}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
